import { AuctionType } from '@/utils/amplitude/analytics/core.types';
import { BuyNowStatus } from '@/types/item/ItemSummary';
import { getCatalogIsCatalogOnly } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import {
    getItemSummaryBuyNowStatus,
    getItemSummaryCatalogId,
    getItemSummaryIndex,
    getItemSummaryIsLiveAuction,
    getItemSummaryIsTimedAuction,
    getItemSummaryIsTimedPlusAuction,
    getItemSummaryPromoted,
    getItemSummarySellerId,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { ItemCardNavigationTrigger, useTrackItemCardNavigationAnalytics } from '@/utils/amplitude/analytics';
import { trackClickForAlgolia } from '@/redux/modules/analytics';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';

type AdditionalParams = Record<string, unknown>;

type ItemCardNavigationPayload = {
    auctionType?: AuctionType;
    catalogId: number;
    houseId: number;
    itemId: number;
    trigger: ItemCardNavigationTrigger;
} & AdditionalParams;

export const useItemCardNavigation = (itemId: number) => {
    const dispatch = useAppDispatch();

    const houseId = useAppSelector((state) => getItemSummarySellerId(state, itemId));
    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const isTimedAuction = useAppSelector((state) => getItemSummaryIsTimedAuction(state, itemId));
    const isTimedPlusAuction = useAppSelector((state) => getItemSummaryIsTimedPlusAuction(state, itemId));
    const isLiveAuction = useAppSelector((state) => getItemSummaryIsLiveAuction(state, itemId));
    const isCatalogOnly = useAppSelector((state) => getCatalogIsCatalogOnly(state, catalogId));
    const buyNowStatus = useAppSelector((state) => getItemSummaryBuyNowStatus(state, itemId));
    const index = useAppSelector((state) => getItemSummaryIndex(state, itemId));
    const isFeatured = useAppSelector((state) => getItemSummaryPromoted(state, itemId));

    const trackItemCardNavigationAnalytics = useTrackItemCardNavigationAnalytics();

    let auctionType: AuctionType | undefined;

    if (buyNowStatus === BuyNowStatus.Available) {
        auctionType = AuctionType.BuyNow;
    } else if (isTimedAuction) {
        auctionType = AuctionType.Timed;
    } else if (isTimedPlusAuction) {
        auctionType = AuctionType.TimedPlus;
    } else if (isLiveAuction) {
        auctionType = AuctionType.Live;
    } else if (isCatalogOnly) {
        auctionType = AuctionType.PreviewOnly;
    }

    const onNavigateInitiated = (trigger: ItemCardNavigationTrigger, additionalParams?: AdditionalParams) => {
        const payload: ItemCardNavigationPayload = {
            catalogId,
            featured: isFeatured,
            houseId,
            itemId,
            trigger,
            ...(auctionType && { auctionType }),
            ...additionalParams,
        };

        if (additionalParams?.promoted) {
            dispatch(trackClickForAlgolia(itemId, (additionalParams?.position as number) ?? index));
        }

        trackItemCardNavigationAnalytics(payload);
    };

    return { auctionType, onNavigateInitiated };
};
